import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { heroSlides } from "@/mocks/videos";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const go = useCallback((next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-foreground-900"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点推荐轮播"
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.videoId}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} - 呱呱视频社区焦点推荐`}
            title={`${slide.title} - 呱呱视频社区`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
        </div>
      ))}

      <div className="relative z-20 h-full w-full flex items-center px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto w-full">
          <div key={index} className="max-w-xl animate-fade-up">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {heroSlides[index].tag}
            </span>
            <h2 className="mt-4 font-heading font-extrabold text-2xl md:text-4xl text-white leading-tight">
              {heroSlides[index].title}
            </h2>
            <p className="mt-3 text-sm md:text-base text-white/80 max-w-lg">
              {heroSlides[index].subtitle}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`/video/${heroSlides[index].videoId}`)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                立即观看
              </button>
              <button
                type="button"
                onClick={() => navigate("/category/short")}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white/15 text-white text-sm font-semibold hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览更多
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.videoId}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}